<template>
    <ModalTheme :overlay="modalOverlay" @close="cancel" :loading="loading || parentLoading">
        <template slot="main">
            <loader :loading="true" v-if="loading"></loader>
            <Navbar @select-tab="selectTab" :type="type" :estore-display="shouldDisplayEstore" :validations="$v.form" v-model="activeTab"></Navbar>
            <GeneralForm v-if="activeTab === TABS.GENERAL" v-model="form.generalForm"></GeneralForm>
            <EditBrandEStore v-if="shouldDisplayEstore && activeTab === TABS.E_STORE" v-model="form.estoreForm" />
            <BrandAccess v-if="activeTab === TABS.BRAND_ACCESS" v-model="form.accessForm" />
        </template>
        <template v-if="modalOverlay" slot="overlay">
            <OverlayErrorMessage :form="$v.form" @close="setModalOverlay(false)"></OverlayErrorMessage>
        </template>
        <template slot="footer">
            <ClientColorButton :inactiveColor="true" @click.prevent="cancel">{{ $t("common.cancel") }}</ClientColorButton>
            <ClientColorButton :inactive-color="$v.form.$invalid" :disabled="$v.form.$invalid" :background-color="brand.linksColor" @click="save"
                >{{ $t("common.save") }}
            </ClientColorButton>
        </template>
    </ModalTheme>
</template>

<script>
import { isColor, isBool } from "@/utils/fieldValidations";
import { required, maxLength, minLength } from "vuelidate/lib/validators";
import { MAX_LENGTH_NAME, MIN_LENGTH_NAME } from "@/utils/string";
import { TABS, FORM_TYPE, DEFAULT_COLORS, generateFormErrorMessage } from "@/components/admin/brand/utils/brandFormUtils";
import TryLoadMixin from "@/mixins/TryLoadMixin";
import loader from "@/components/loader/loader";

import BrandAccess from "@/components/admin/brand/tabs/BrandAccess";
import EditBrandEStore from "@/components/admin/brand/tabs/EditBrandEStore";
import ClientColorButton from "@/components/ui/ClientColorButton";
import ModalBase from "@/components/modal/ModalBase";
import GeneralForm from "@/components/admin/brand/tabs/GeneralForm";
import OverlayErrorMessage from "@/components/admin/brand/components/OverlayErrorMessage";
import Navbar from "@/components/admin/brand/components/Navbar";
import ModalTheme from "@/components/ui/ModalTheme";
import { mapActions } from "vuex";
import {removeNbsp} from "../../../utils/string";

const isArray = Array.isArray;

export default {
    data() {
        return {
            FORM_TYPE,
            actionUrl: `/admin/brands/`,
            uploadFieldName: "file",
            activeTab: TABS.GENERAL,
            TABS,
            modalOverlay: false,
            form: {
                generalForm: {
                    name: undefined,
                    linksColor: DEFAULT_COLORS.linksColor,
                    backgroundColor: DEFAULT_COLORS.backgroundColor,
                    thirdColor: DEFAULT_COLORS.thirdColor,
                    fourthColor: DEFAULT_COLORS.fourthColor,
                    theme: "light",
                    logo: undefined,
                    heroImg: undefined,
                    logoBase64: undefined,
                    heroImgBase64: undefined
                },
                estoreForm: {
                    isPriceShown: false,
                    currency: {
                        id: 14
                    },
                    orderShippingMethods: []
                },
                accessForm: {
                    brandAccesses: []
                }
            }
        };
    },
    props: {
        brand: {
            type: Object
        },
        type: {
            required: true,
            default: FORM_TYPE.edit,
            type: String,
            validator: type => type === FORM_TYPE.edit || type === FORM_TYPE.create
        },
        parentLoading: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    mixins: [TryLoadMixin],
    components: {
        Loader: loader,
        EditBrandEStore,
        BrandAccess,
        ClientColorButton,
        ModalBase,
        GeneralForm,
        Navbar,
        ModalTheme,
        OverlayErrorMessage
    },
    validations() {
        let estoreForm = this.shouldDisplayEstore
            ? {
                  isPriceShown: { isBool },
                  currency: { id: { required } },
                  orderShippingMethods: { isArray }
              }
            : {};
        return {
            form: {
                generalForm: {
                    name: { required, max: maxLength(MAX_LENGTH_NAME), min: minLength(MIN_LENGTH_NAME) },
                    linksColor: { isColor, required },
                    backgroundColor: { isColor, required },
                    thirdColor: { isColor, required },
                    fourthColor: { isColor, required },
                    theme: { required },
                    logo: { required }
                },
                accessForm: {
                    brandAccesses: { isArray }
                },
                estoreForm
            }
        };
    },
    methods: {
        ...mapActions("functionality", {
            findAllApplications: "findAllApplications"
        }),
        selectTab(tab) {
            this.activeTab = tab;
        },
        setModalOverlay(value) {
            this.modalOverlay = value;
        },
        save() {
            // Updates this.$v model
            this.$v.form.$touch();

            if (this.$v.form.$invalid) {
                let message = "";
                // Get all form names from validator object
                const formNames = Object.keys(this.$v.form.$params);
                for (const formName of formNames) {
                    const form = this.$v.form[formName];
                    if (form.$invalid) message += generateFormErrorMessage(formName, form);
                }
                this.setModalOverlay(true);
                return false;
            }
            this.form.accessForm.brandAccesses = this.form.accessForm.brandAccesses.filter(ba => ba.active);
            this.form.generalForm.name = removeNbsp(this.form.generalForm.name);
            this.$emit("save", {
                ...this.brand,
                ...this.form.generalForm,
                ...this.form.estoreForm,
                ...this.form.accessForm
            });
        },
        cancel() {
            this.$emit("cancel");
        },
        loadBrandDatas() {
            if (Boolean(this.brand.id)) {
                const {
                    name,
                    logo,
                    orderShippingMethods,
                    heroImg,
                    linksColor,
                    backgroundColor,
                    thirdColor,
                    fourthColor,
                    theme,
                    imgPath,
                    isPriceShown,
                    currency,
                    brandAccesses
                } = this.brand;
                this.form.generalForm = { name, logo, heroImg, imgPath, linksColor, backgroundColor, thirdColor, fourthColor, theme };
                this.form.estoreForm = { isPriceShown, currency, orderShippingMethods };
                this.form.accessForm = { brandAccesses };
            }
        }
    },
    computed: {
        shouldDisplayEstore() {
            return Boolean(this.form.accessForm.brandAccesses && this.form.accessForm.brandAccesses.find(application => application === 3));
        }
    },
    async created() {
        this.loadBrandDatas();
    }
};
</script>

<style lang="scss" scoped>
.title {
    font-family: "Oswald", sans-serif;
    font-size: 30px;
    text-transform: uppercase;
}
</style>



// WEBPACK FOOTER //
// BrandForm.vue?2aebfbe9